import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { getProcess, type ProcessDetailDto } from '@api/processes';

const PROCESS_DETAIL_KEY_ROOT = ['processes', 'detail'] as const;

/**
 * Cache key for a single process's detail (incl. graph). Importing this from
 * other hooks keeps the key consistent across `useProcessActions` (PR4) when
 * we'll need to invalidate it after a save.
 */
export const processDetailQueryKey = (
  processId: string,
): readonly unknown[] => [...PROCESS_DETAIL_KEY_ROOT, processId];

/**
 * React Query wrapper around `GET /processes/:id`. Disabled when no
 * `processId` is given (the editor route guard renders an empty state in
 * that case). The result is the full `ProcessDetailDto` — process metadata
 * plus the graph (nodes + edges) inline, mirroring plan §13.4.
 *
 * Network errors short-circuit retry — the editor's error state shows the
 * message immediately rather than spending 3 attempts to find out the
 * process doesn't exist.
 */
export function useProcessDetail(
  processId: string | undefined,
): UseQueryResult<ProcessDetailDto, Error> {
  return useQuery<ProcessDetailDto, Error>({
    queryKey: processDetailQueryKey(processId ?? ''),
    queryFn: () => getProcess(processId!),
    enabled: Boolean(processId),
    retry: 1,
    staleTime: 30_000,
  });
}
